import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Badge,
  Tabs,
  Tab,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ChatIcon from '@mui/icons-material/Chat';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import {
  ConversationList,
  ConversationHeader,
  MessageThread,
  MessageComposer,
  DeliveryStatusPanel,
  IntakeReviewCard,
  EmailThread,
  LinkEmailCustomerDialog,
} from '@/components/messages';
import type { IntakeReviewActionPayload } from '@/components/messages';
import type { TemplateSendPayload } from '@/components/messages/MessageComposer';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useNotify } from '@/stores/notificationStore';
import { useWebSocket } from '@/hooks/useWebSocket';
import { queryKeys } from '@/hooks/queryKeys';
import { useOpenThreadSync } from '@/hooks/useOpenThreadSync';
import { useWhatsAppTemplates } from '@/hooks/useWhatsAppTemplates';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { useDebounce } from '@/hooks/useDebounce';
import { requiresTemplate, sessionWindowFromError, windowOpenedBy } from '@/utils/sessionWindow';
import { appendUniqueById, conversationListParams } from '@/utils/appendUniqueById';
import { mergeMessageLists } from '@/utils/mergeMessages';
import { getConversationTitle } from '@/utils/conversationParty';
import { useAuthStore } from '@/stores/authStore';
import { ConversationMediaError, sendConversationMedia } from '@/utils/conversationMedia';
import type { Conversation, Message, SessionWindow } from '@/types';
import NewConversationDialog from './NewConversationDialog';

type FilterTab = 'all' | 'unread' | 'whatsapp' | 'email' | 'delivery' | 'review';
/** Mailbox email folders (#3736): spam, auto-replies and bulk mail live in `filtered`. */
type EmailFolder = 'inbox' | 'filtered';

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    nextCursor: string | null;
    prevCursor: string | null;
    hasMore: boolean;
  };
}

export default function MessagesPage() {
  const { t } = useTranslation('messages');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversationsError, setConversationsError] = useState(false);
  const [conversationsCursor, setConversationsCursor] = useState<string | null>(null);
  const [conversationsHasMore, setConversationsHasMore] = useState(false);
  const [conversationsLoadingMore, setConversationsLoadingMore] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesCursor, setMessagesCursor] = useState<string | null>(null);
  const [messagesHasMore, setMessagesHasMore] = useState(false);
  const [messagesLoadingOlder, setMessagesLoadingOlder] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTab, setFilterTab] = useState<FilterTab>('all');
  const [reviewBusy, setReviewBusy] = useState(false);
  const [typingIndicator, setTypingIndicator] = useState<{ conversationId: string; customerName: string } | null>(null);
  const [newConversationOpen, setNewConversationOpen] = useState(false);
  const [emailFolder, setEmailFolder] = useState<EmailFolder>('inbox');
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  // Without WhatsApp only customer portal chats can be answered (#2830).
  const { whatsappAvailable, mailboxInboxEnabled } = useTenantSettings();
  // Workshop mailbox email (#3736) needs messages.email on top of messages.read,
  // and the tab only means something where the tenant routes its mailbox here.
  const hasEmailPermission = useAuthStore((s) => s.hasPermission('messages.email'));
  const showEmailTab = hasEmailPermission && mailboxInboxEnabled;
  const loadGeneration = useRef(0);

  const debouncedSearch = useDebounce(searchQuery, 300);
  const listChannel = filterTab === 'whatsapp' ? 'whatsapp' : filterTab === 'email' ? 'email' : undefined;
  const listQueue = filterTab === 'review' ? 'review' : undefined;
  const listMailbox = filterTab === 'email' && emailFolder === 'filtered' ? 'filtered' : undefined;

  const applyConversationPage = useCallback((
    response: PaginatedResponse<Conversation>,
    reset: boolean,
  ) => {
    setConversations((prev) => (reset ? response.data : appendUniqueById(prev, response.data)));
    setConversationsCursor(response.pagination.nextCursor);
    setConversationsHasMore(response.pagination.hasMore);
    setConversationsError(false);
  }, []);

  useEffect(() => {
    const generation = ++loadGeneration.current;
    async function loadConversations() {
      try {
        const response = await api.get<PaginatedResponse<Conversation>>('/conversations', {
          params: conversationListParams({
            q: debouncedSearch,
            ...(listChannel ? { channel: listChannel } : {}),
            ...(listQueue ? { queue: listQueue } : {}),
            ...(listMailbox ? { mailbox: listMailbox } : {}),
          }),
        });
        if (generation !== loadGeneration.current) return;
        applyConversationPage(response, true);
      } catch {
        if (generation !== loadGeneration.current) return;
        setConversationsError(true);
        setConversations([]);
        setConversationsCursor(null);
        setConversationsHasMore(false);
      }
    }
    void loadConversations();
  }, [applyConversationPage, debouncedSearch, listChannel, listQueue, listMailbox]);

  const handleLoadMoreConversations = useCallback(async () => {
    if (!conversationsCursor || conversationsLoadingMore) return;
    setConversationsLoadingMore(true);
    try {
      const response = await api.get<PaginatedResponse<Conversation>>('/conversations', {
        params: conversationListParams({
          cursor: conversationsCursor,
          q: debouncedSearch,
          ...(listChannel ? { channel: listChannel } : {}),
          ...(listQueue ? { queue: listQueue } : {}),
          ...(listMailbox ? { mailbox: listMailbox } : {}),
        }),
      });
      applyConversationPage(response, false);
    } catch {
      notify.error(tc('error'), t('conversationsLoadFailed'));
    } finally {
      setConversationsLoadingMore(false);
    }
  }, [
    applyConversationPage,
    conversationsCursor,
    conversationsLoadingMore,
    debouncedSearch,
    listChannel,
    listMailbox,
    listQueue,
    notify,
    t,
    tc,
  ]);

  /** Apply a session-window change to both the list entry and the open thread. */
  const applySessionWindow = useCallback((conversationId: string, sessionWindow: SessionWindow) => {
    setConversations((prev) =>
      prev.map((c) => (c.id === conversationId ? { ...c, sessionWindow } : c))
    );
    setSelectedConversation((prev) =>
      prev && prev.id === conversationId ? { ...prev, sessionWindow } : prev
    );
  }, []);

  const applyLane = useCallback((
    conversationId: string,
    lane: Partial<Pick<Conversation, 'staffQueueStatus' | 'customerPhase' | 'agentAuthority' | 'intakeSummary'>>,
  ) => {
    setConversations((prev) =>
      prev.map((c) => (c.id === conversationId ? { ...c, ...lane } : c)),
    );
    setSelectedConversation((prev) =>
      prev && prev.id === conversationId ? { ...prev, ...lane } : prev,
    );
  }, []);

  // WebSocket: Listen for new messages
  useWebSocket('message:new', useCallback((message: Message) => {
    // Add message to the thread if conversation is selected
    if (selectedConversation && message.conversationId === selectedConversation.id) {
      setMessages((prev) => mergeMessageLists(prev, [message]));
    }

    // An inbound message reopens the 24 h window (#1471) — reflect it right away
    // instead of leaving the composer in template mode until the next fetch.
    const reopened =
      message.direction === 'inbound' && message.channel === 'whatsapp'
        ? windowOpenedBy(message.sentAt)
        : null;

    // Update conversation's last message
    setConversations((prev) =>
      prev.map((c) =>
        c.id === message.conversationId
          ? {
              ...c,
              lastMessage: message,
              updatedAt: message.sentAt,
              unreadCount: c.id === selectedConversation?.id ? 0 : c.unreadCount + 1,
              ...(reopened ? { sessionWindow: reopened } : {}),
            }
          : c
      )
    );

    if (reopened) {
      setSelectedConversation((prev) =>
        prev && prev.id === message.conversationId ? { ...prev, sessionWindow: reopened } : prev
      );
    }

    // Show notification for new messages
    if (message.direction === 'inbound' && message.conversationId !== selectedConversation?.id) {
      notify.info(t('newMessage'), message.content.substring(0, 50));
    }
  }, [selectedConversation, notify, t]));

  useOpenThreadSync(selectedConversation?.id ?? null, setMessages);

  // Keep the list preview and 24 h window in sync when poll/WS adds a message.
  useEffect(() => {
    if (!selectedConversation || messages.length === 0) {
      return;
    }
    const last = messages[messages.length - 1];
    if (!last) {
      return;
    }

    setConversations((prev) =>
      prev.map((conversation) => {
        if (conversation.id !== selectedConversation.id) {
          return conversation;
        }
        if (conversation.lastMessage?.id === last.id) {
          return conversation;
        }
        return { ...conversation, lastMessage: last, updatedAt: last.sentAt };
      }),
    );

    if (last.direction === 'inbound' && last.channel === 'whatsapp') {
      const nextWindow = windowOpenedBy(last.sentAt);
      if (selectedConversation.sessionWindow?.lastInboundAt !== nextWindow.lastInboundAt) {
        applySessionWindow(selectedConversation.id, nextWindow);
      }
    }
  }, [applySessionWindow, messages, selectedConversation]);

  // WebSocket: Listen for typing indicators
  useWebSocket('typing:start', useCallback((data: { conversationId: string; customerName: string }) => {
    if (selectedConversation?.id === data.conversationId) {
      setTypingIndicator(data);
    }
  }, [selectedConversation]));

  useWebSocket('typing:stop', useCallback((data: { conversationId: string }) => {
    if (selectedConversation?.id === data.conversationId) {
      setTypingIndicator(null);
    }
  }, [selectedConversation]));

  const filteredConversations = useMemo(() => {
    return conversations.filter((conv) => {
      if (filterTab === 'unread' && conv.unreadCount === 0) return false;
      if (filterTab === 'delivery') return false;
      return true;
    });
  }, [conversations, filterTab]);

  const unreadCount = useMemo(
    () => conversations.reduce((sum, c) => sum + c.unreadCount, 0),
    [conversations]
  );

  // Templates are only fetched when a closed window actually forces them.
  const templateMode = selectedConversation ? requiresTemplate(selectedConversation) : false;
  const templatesQuery = useWhatsAppTemplates(templateMode);

  const handleSelectConversation = useCallback(async (conversation: Conversation) => {
    setSelectedConversation(conversation);

    // Refresh session window and intake so the review card is not stale.
    try {
      const fresh = await api.get<Conversation>(`/conversations/${conversation.id}`);
      applyLane(conversation.id, {
        ...(fresh.staffQueueStatus !== undefined ? { staffQueueStatus: fresh.staffQueueStatus } : {}),
        ...(fresh.customerPhase !== undefined ? { customerPhase: fresh.customerPhase } : {}),
        ...(fresh.agentAuthority !== undefined ? { agentAuthority: fresh.agentAuthority } : {}),
        ...(fresh.intakeSummary !== undefined ? { intakeSummary: fresh.intakeSummary } : {}),
      });
      if (fresh.sessionWindow) {
        applySessionWindow(conversation.id, fresh.sessionWindow);
      }
    } catch {
      // Keep the list snapshot; a stale window still self-corrects on send.
    }

    try {
      const response = await api.get<PaginatedResponse<Message>>(
        `/conversations/${conversation.id}/messages`
      );
      setMessages(response.data);
      setMessagesCursor(response.pagination.nextCursor);
      setMessagesHasMore(response.pagination.hasMore);
    } catch {
      setMessages([]);
      setMessagesCursor(null);
      setMessagesHasMore(false);
      notify.error(tc('error'), t('messagesLoadFailed'));
    }

    // Mark as read
    if (conversation.unreadCount > 0) {
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversation.id ? { ...c, unreadCount: 0 } : c
        )
      );

      void api.post(`/conversations/${conversation.id}/read`).then(
        () => {
          void queryClient.invalidateQueries({
            queryKey: queryKeys.conversations.unreadCount(),
          });
        },
        () => undefined,
      );
    }
  }, [notify, t, tc, applySessionWindow, applyLane, queryClient]);

  const handleLoadOlderMessages = useCallback(async () => {
    if (!selectedConversation || !messagesCursor || messagesLoadingOlder) return;
    setMessagesLoadingOlder(true);
    try {
      const response = await api.get<PaginatedResponse<Message>>(
        `/conversations/${selectedConversation.id}/messages`,
        { params: { cursor: messagesCursor } },
      );
      setMessages((prev) => mergeMessageLists(prev, response.data));
      setMessagesCursor(response.pagination.nextCursor);
      setMessagesHasMore(response.pagination.hasMore);
    } catch {
      notify.error(tc('error'), t('messagesLoadFailed'));
    } finally {
      setMessagesLoadingOlder(false);
    }
  }, [messagesCursor, messagesLoadingOlder, notify, selectedConversation, t, tc]);

  const registerSentMessage = useCallback((conversationId: string, newMsg: Message) => {
    setMessages((prev) => mergeMessageLists(prev, [newMsg]));
    setConversations((prev) =>
      prev.map((c) =>
        c.id === conversationId ? { ...c, lastMessage: newMsg, updatedAt: newMsg.sentAt } : c
      )
    );
    notify.success(t('messageSent'), t('messageSentDescription'));
  }, [notify, t]);

  const handleSendMessage = useCallback(async (content: string) => {
    if (!selectedConversation) return;

    try {
      const newMsg = await api.post<Message>(
        `/conversations/${selectedConversation.id}/messages`,
        { content, type: 'text' }
      );
      registerSentMessage(selectedConversation.id, newMsg);
    } catch (error) {
      // A closed 24 h window is not a generic failure: say so and flip the
      // composer to template mode so the reply can actually be sent (#1471).
      const closedWindow = sessionWindowFromError(error);
      if (closedWindow) {
        applySessionWindow(selectedConversation.id, closedWindow);
        notify.error(t('sessionWindowClosed'), t('sessionWindowClosedComposerHint'));
        return;
      }
      notify.error(t('messageSendFailed'));
    }
  }, [selectedConversation, notify, t, registerSentMessage, applySessionWindow]);

  const handleSendInternal = useCallback(async (content: string) => {
    if (!selectedConversation) return;
    try {
      const newMsg = await api.post<Message>(
        `/conversations/${selectedConversation.id}/messages`,
        { content, type: 'text', lane: 'internal' },
      );
      setMessages((prev) => mergeMessageLists(prev, [newMsg]));
      notify.success(t('internalNoteSaved'), t('internalNoteSavedDesc'));
    } catch {
      notify.error(t('messageSendFailed'));
    }
  }, [selectedConversation, notify, t]);

  const handleReviewAction = useCallback(async (payload: IntakeReviewActionPayload) => {
    if (!selectedConversation) return;
    setReviewBusy(true);
    try {
      const result = await api.post<{
        staffQueueStatus: string;
        customerPhase: string;
        agentAuthority: string;
        customerMessage?: { id: string; content: string };
      }>(`/conversations/${selectedConversation.id}/staff-decisions`, payload);
      applyLane(selectedConversation.id, {
        staffQueueStatus: result.staffQueueStatus,
        customerPhase: result.customerPhase,
        agentAuthority: result.agentAuthority,
      });
      if (result.customerMessage) {
        const relayed = result.customerMessage;
        setMessages((prev) => mergeMessageLists(prev, [{
          id: relayed.id,
          conversationId: selectedConversation.id,
          direction: 'outbound',
          channel: selectedConversation.channel,
          content: relayed.content,
          status: 'sent',
          sentAt: new Date().toISOString(),
        }]));
      }
      const successKey =
        payload.action === 'claim'
          ? 'reviewClaimed'
          : payload.action === 'ask_customer'
            ? 'reviewAsked'
            : payload.action === 'block'
              ? 'reviewBlocked'
              : 'reviewClosed';
      notify.success(t(successKey));
    } catch {
      notify.error(t('reviewActionFailed'));
    } finally {
      setReviewBusy(false);
    }
  }, [applyLane, notify, selectedConversation, t]);

  const handleSendFile = useCallback(async (file: File, caption: string) => {
    if (!selectedConversation) return;
    try {
      const newMsg = await sendConversationMedia(selectedConversation.id, file, caption);
      registerSentMessage(selectedConversation.id, newMsg);
    } catch (error) {
      const closedWindow = sessionWindowFromError(error);
      if (closedWindow) {
        applySessionWindow(selectedConversation.id, closedWindow);
        notify.error(t('sessionWindowClosed'), t('sessionWindowClosedComposerHint'));
        return;
      }
      if (error instanceof ConversationMediaError && error.code === 'unsupported') {
        notify.error(t('attachmentUnsupported'));
        return;
      }
      if (error instanceof ConversationMediaError && error.code === 'too_large') {
        notify.error(t('attachmentTooLarge'));
        return;
      }
      notify.error(t('messageSendFailed'));
    }
  }, [selectedConversation, notify, t, registerSentMessage, applySessionWindow]);

  /** Re-read a conversation after its email thread was linked to a customer (#3736). */
  const handleEmailLinked = useCallback(async () => {
    if (!selectedConversation) return;
    setLinkDialogOpen(false);
    try {
      const fresh = await api.get<Conversation>(`/conversations/${selectedConversation.id}`);
      const merge = (c: Conversation): Conversation =>
        c.id === fresh.id ? { ...c, customerId: fresh.customerId, customer: fresh.customer, email: fresh.email ?? null } : c;
      setConversations((prev) => prev.map(merge));
      setSelectedConversation((prev) => (prev ? merge(prev) : prev));
    } catch {
      // The link is saved; the list shows it on the next load.
    }
  }, [selectedConversation]);

  const handleConversationCreated = useCallback(async (conversation: Conversation) => {
    setConversations((prev) => [conversation, ...prev.filter((item) => item.id !== conversation.id)]);
    await handleSelectConversation(conversation);
  }, [handleSelectConversation]);

  const handleSendTemplate = useCallback(async (payload: TemplateSendPayload) => {
    if (!selectedConversation) return;

    try {
      const newMsg = await api.post<Message>(
        `/conversations/${selectedConversation.id}/messages`,
        { type: 'template', ...payload }
      );
      registerSentMessage(selectedConversation.id, newMsg);
    } catch {
      notify.error(t('templateSendFailed'));
    }
  }, [selectedConversation, notify, t, registerSentMessage]);

  return (
    <Box sx={{ height: { xs: 'calc(100vh - 180px - 64px - env(safe-area-inset-bottom, 0px))', md: 'calc(100vh - 140px)' }, display: 'flex', flexDirection: 'column' }}>
      {!isMobile && (
        <Typography variant="h4" fontWeight={600} gutterBottom>
          {t('title')}
        </Typography>
      )}

      <Paper
        elevation={0}
        sx={{
          flexGrow: 1,
          display: 'flex',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Sidebar - Conversation List */}
        <Box
          sx={{
            width: { xs: '100%', md: 360 },
            borderRight: { xs: 0, md: 1 },
            borderColor: 'divider',
            display: {
              xs: selectedConversation ? 'none' : 'flex',
              md: 'flex',
            },
            flexDirection: 'column',
          }}
        >
          {/* Search */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            {whatsappAvailable && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setNewConversationOpen(true)}
                aria-label={t('newConversation')}
                sx={{ flexShrink: 0 }}
              >
                {t('newConversation')}
              </Button>
            )}
          </Box>

          {/* Filter Tabs */}
          <Tabs
            value={filterTab}
            onChange={(_e, value) => setFilterTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="all" label={t('all')} />
            <Tab
              value="unread"
              label={
                <Badge badgeContent={unreadCount} color="error" sx={{ pr: 1.5 }}>
                  {t('unread')}
                </Badge>
              }
            />
            <Tab value="review" label={t('reviewQueue')} />
            <Tab value="whatsapp" label="WhatsApp" />
            {showEmailTab && <Tab value="email" label={t('emailTab')} />}
            <Tab value="delivery" icon={<MonitorHeartIcon fontSize="small" />} iconPosition="start" label={t('deliveryTab')} />
          </Tabs>

          {filterTab === 'email' && (
            <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                fullWidth
                value={emailFolder}
                onChange={(_e, value: EmailFolder | null) => {
                  if (value) setEmailFolder(value);
                }}
              >
                <ToggleButton value="inbox">{t('emailInbox')}</ToggleButton>
                <ToggleButton value="filtered">{t('emailFiltered')}</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {/* Conversations or Delivery Panel */}
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            {filterTab === 'delivery' ? (
              <DeliveryStatusPanel
                onNavigateToConversation={(convId) => {
                  const conv = conversations.find((c) => c.id === convId);
                  if (conv) {
                    setFilterTab('all');
                    void handleSelectConversation(conv);
                  }
                }}
              />
            ) : (
              <ConversationList
                conversations={filteredConversations}
                selectedId={selectedConversation?.id}
                onSelect={handleSelectConversation}
                error={conversationsError}
                hasMore={conversationsHasMore}
                loadingMore={conversationsLoadingMore}
                onLoadMore={() => { void handleLoadMoreConversations(); }}
              />
            )}
          </Box>
        </Box>

        {/* Main - Chat Area */}
        <Box
          sx={{
            flexGrow: 1,
            display: {
              xs: selectedConversation ? 'flex' : 'none',
              md: 'flex',
            },
            flexDirection: 'column',
            backgroundColor: 'action.hover',
          }}
        >
          {selectedConversation ? (
            <>
              <ConversationHeader
                conversation={selectedConversation}
                onBack={() => setSelectedConversation(null)}
                {...(selectedConversation.channel === 'email'
                  ? { onLinkCustomer: () => setLinkDialogOpen(true) }
                  : {})}
              />
              {selectedConversation.channel === 'email' ? (
                <>
                  <EmailThread
                    messages={messages}
                    hasOlder={messagesHasMore}
                    loadingOlder={messagesLoadingOlder}
                    onLoadOlder={() => { void handleLoadOlderMessages(); }}
                  />
                  {/* Replying from OS Buddy is #3737. */}
                  <Alert severity="info" sx={{ borderRadius: 0 }}>
                    {t('emailReplyNotYet')}
                  </Alert>
                  <LinkEmailCustomerDialog
                    open={linkDialogOpen}
                    conversationId={selectedConversation.id}
                    senderAddress={selectedConversation.email?.address ?? null}
                    onClose={() => setLinkDialogOpen(false)}
                    onLinked={() => { void handleEmailLinked(); }}
                  />
                </>
              ) : (
                <>
                  <IntakeReviewCard
                    conversation={selectedConversation}
                    onAction={handleReviewAction}
                    busy={reviewBusy}
                  />
                  <MessageThread
                    messages={messages}
                    customerName={getConversationTitle(selectedConversation, t('unknownContact'))}
                    typingIndicator={typingIndicator}
                    hasOlder={messagesHasMore}
                    loadingOlder={messagesLoadingOlder}
                    onLoadOlder={() => { void handleLoadOlderMessages(); }}
                  />
                  <MessageComposer
                    onSend={handleSendMessage}
                    onSendInternal={handleSendInternal}
                    // The customer portal chat shows text only (#2828).
                    {...(selectedConversation.channel === 'portal' ? {} : { onSendFile: handleSendFile })}
                    onSendTemplate={handleSendTemplate}
                    templateMode={templateMode}
                    {...(templatesQuery.data ? { templates: templatesQuery.data } : {})}
                    templatesLoading={templatesQuery.isLoading}
                    templatesError={templatesQuery.isError}
                    {...(!whatsappAvailable && selectedConversation.channel !== 'portal'
                      ? { disabled: true, placeholder: t('whatsappNotConnectedComposer') }
                      : {})}
                  />
                </>
              )}
            </>
          ) : (
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              <ChatIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
              <Typography variant="h6">{t('selectConversation')}</Typography>
              <Typography variant="body2">{t('selectConversationHint')}</Typography>
            </Box>
          )}
        </Box>
      </Paper>
      <NewConversationDialog
        open={newConversationOpen}
        onClose={() => setNewConversationOpen(false)}
        onCreated={(conversation) => { void handleConversationCreated(conversation); }}
      />
    </Box>
  );
}
